import { useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Clock,
  Download,
  Grid,
  List
} from 'lucide-react';
import { ResponsiveTable, StatusBadge } from '../../components/ui/table';
import { Button } from '../../components/ui/Button/Button';
import { Card } from '../../components/ui/Card';
import { TripCard, TripCardCompact } from '../../components/trips/TripCard';
import { TripFilters } from '../../components/trips/TripFilters';
import { TripDetailsModal } from '../../components/trips/TripDetailsModal';
// import { useMemoizedTripFilters, useDebouncedSearch } from '../../components/trips/TripOptimizations';
import { TRIP_STATUS, TRIP_TYPE, completeTrip, cancelTrip } from '../../store/tripSlice';

export function TripHistoryPage() {
  const dispatch = useDispatch();
  const trips = useSelector(state => state.trips.trips);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [dateRange, setDateRange] = useState('all');
  const [viewMode, setViewMode] = useState('table'); // 'table', 'cards', 'compact'
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  // Filter and search trips
  const filteredTrips = useMemo(() => {
    let filtered = trips;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(trip =>
        trip.passenger.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        trip.driver?.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        trip.pickup.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        trip.dropoff.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        trip.id.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(trip => trip.status === statusFilter);
    }

    // Type filter
    if (typeFilter !== 'all') {
      filtered = filtered.filter(trip => trip.type === typeFilter);
    }

    // Date range filter
    if (dateRange !== 'all') {
      const now = new Date();
      let startDate = new Date();

      switch (dateRange) {
        case 'today':
          startDate.setHours(0, 0, 0, 0);
          break;
        case 'week':
          startDate.setDate(now.getDate() - 7);
          break;
        case 'month':
          startDate.setMonth(now.getMonth() - 1);
          break;
        default:
          startDate = null;
      }

      if (startDate) {
        filtered = filtered.filter(trip => new Date(trip.requestedAt) >= startDate);
      }
    }

    return [...filtered].sort((a, b) => new Date(b.requestedAt) - new Date(a.requestedAt));
  }, [trips, searchTerm, statusFilter, typeFilter, dateRange]);

  const handleViewDetails = (trip) => {
    setSelectedTrip(trip);
    setShowDetailsModal(true);
  };

  const handleCompleteTrip = (tripId) => {
    dispatch(completeTrip({ tripId }));
  };

  const handleCancelTrip = (tripId) => {
    dispatch(cancelTrip({ tripId, reason: 'Cancelled by admin' }));
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setTypeFilter('all');
    setDateRange('all');
  };

  const renderContent = () => {
    if (filteredTrips.length === 0) {
      return (
        <Card className="p-12 text-center">
          <Clock className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No trips found</h3>
          <p className="text-gray-500">
            {searchTerm || statusFilter !== 'all' || typeFilter !== 'all' || dateRange !== 'all'
              ? 'Try adjusting your filters to see more trips.'
              : 'Trip history will appear here once trips are completed.'
            }
          </p>
        </Card>
      );
    }

    switch (viewMode) {
      case 'cards':
        return (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
            {filteredTrips.map((trip) => (
              <TripCard
                key={trip.id}
                trip={trip}
                onViewDetails={handleViewDetails}
                onCompleteTrip={handleCompleteTrip}
                onCancelTrip={handleCancelTrip}
              />
            ))}
          </div>
        );

      case 'compact':
        return (
          <Card className="divide-y divide-gray-200">
            {filteredTrips.map((trip) => (
              <TripCardCompact
                key={trip.id}
                trip={trip}
                onViewDetails={handleViewDetails}
              />
            ))}
          </Card>
        );

      default: // table
        const columns = [
          {
            key: 'id',
            header: 'Trip ID',
            primary: true,
            render: (value) => (
              <div className="font-medium text-gray-900">
                #{value.slice(-6)}
              </div>
            )
          },
          {
            key: 'passenger',
            header: 'Passenger',
            render: (passenger) => (
              <div>
                <div className="font-medium text-gray-900">{passenger.name}</div>
                <div className="text-sm text-gray-500">{passenger.phone}</div>
              </div>
            )
          },
          {
            key: 'driver',
            header: 'Driver',
            render: (driver) => driver ? (
              <div>
                <div className="font-medium text-gray-900">{driver.name}</div>
                <div className="text-sm text-gray-500">{driver.vehicle}</div>
              </div>
            ) : (
              <span className="text-gray-400">Not assigned</span>
            )
          },
          {
            key: 'route',
            header: 'Route',
            render: (_, trip) => (
              <div className="space-y-1">
                <div className="flex items-center text-sm">
                  <div className="w-2 h-2 rounded-full bg-green-500 mr-2"></div>
                  <span className="text-gray-900">{trip.pickup.name}</span>
                </div>
                <div className="flex items-center text-sm">
                  <div className="w-2 h-2 rounded-full bg-red-500 mr-2"></div>
                  <span className="text-gray-900">{trip.dropoff.name}</span>
                </div>
              </div>
            )
          },
          {
            key: 'status',
            header: 'Status',
            render: (status) => {
              const statusConfig = {
                [TRIP_STATUS.COMPLETED]: { variant: 'success', label: 'Completed' },
                [TRIP_STATUS.CANCELLED]: { variant: 'danger', label: 'Cancelled' },
                [TRIP_STATUS.ONGOING]: { variant: 'info', label: 'In Progress' },
                [TRIP_STATUS.ASSIGNED]: { variant: 'warning', label: 'Assigned' },
                [TRIP_STATUS.REQUESTED]: { variant: 'default', label: 'Requested' }
              };

              const config = statusConfig[status] || { variant: 'default', label: status };
              return <StatusBadge status={config.label} variant={config.variant} />;
            }
          },
          {
            key: 'fare',
            header: 'Fare',
            render: (fare) => (
              <div className="text-right">
                <div className="font-medium text-gray-900">${fare.total}</div>
                {fare.surge > 1 && (
                  <div className="text-xs text-orange-600">
                    {fare.surge}x surge
                  </div>
                )}
              </div>
            )
          },
          {
            key: 'requestedAt',
            header: 'Date',
            render: (date) => (
              <div className="text-sm">
                <div className="text-gray-900">
                  {new Date(date).toLocaleDateString()}
                </div>
                <div className="text-gray-500">
                  {new Date(date).toLocaleTimeString('en-US', {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </div>
              </div>
            )
          }
        ];

        const actions = [
          {
            type: 'button',
            label: 'View Details',
            onClick: handleViewDetails,
            variant: 'primary'
          }
        ];

        return (
          <Card>
            <ResponsiveTable
              data={filteredTrips}
              columns={columns}
              actions={actions}
              emptyMessage="No trips found matching your criteria"
            />
          </Card>
        );
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Trip History</h1>
          <p className="mt-2 text-gray-600">
            Complete record of all trips with detailed information and analytics
          </p>
        </div>
        <div className="flex items-center space-x-3 mt-4 sm:mt-0">
          {/* View Mode Toggle */}
          <div className="flex items-center bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('table')}
              className={`p-2 rounded-md transition-colors ${
                viewMode === 'table' ? 'bg-white shadow-sm' : 'hover:bg-gray-200'
              }`}
              title="Table View"
            >
              <List className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('cards')}
              className={`p-2 rounded-md transition-colors ${
                viewMode === 'cards' ? 'bg-white shadow-sm' : 'hover:bg-gray-200'
              }`}
              title="Card View"
            >
              <Grid className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('compact')}
              className={`p-2 rounded-md transition-colors ${
                viewMode === 'compact' ? 'bg-white shadow-sm' : 'hover:bg-gray-200'
              }`}
              title="Compact View"
            >
              <Clock className="w-4 h-4" />
            </button>
          </div>

          <Button variant="outline">
            <Download className="w-4 h-4 mr-2" />
            Export Data
          </Button>
        </div>
      </div>

      {/* Filters */}
      <TripFilters
        searchTerm={searchTerm}
        statusFilter={statusFilter}
        typeFilter={typeFilter}
        dateRange={dateRange}
        onSearchChange={setSearchTerm}
        onStatusChange={setStatusFilter}
        onTypeChange={setTypeFilter}
        onDateRangeChange={setDateRange}
        onClearFilters={handleClearFilters}
        totalTrips={trips.length}
        filteredTrips={filteredTrips.length}
      />

      {/* Content */}
      {renderContent()}

      {/* Trip Details Modal */}
      <TripDetailsModal
        trip={selectedTrip}
        isOpen={showDetailsModal}
        onClose={() => {
          setShowDetailsModal(false);
          setSelectedTrip(null);
        }}
        onCompleteTrip={handleCompleteTrip}
        onCancelTrip={handleCancelTrip}
      />
    </div>
  );
}
